import React, { useEffect, useState } from 'react';
import { Utensils, Crown, Sparkles, Star, ArrowRight } from 'lucide-react';
import { useDocumentTitle } from '../useDocumentTitle';
import topHtml from '../content/meal-plans-top.html?raw';
import bottomHtml from '../content/meal-plans-bottom.html?raw';
import { SUBSCRIPTION_BUTTON_IDS, TRIAL_PAGE_URLS, RazorpaySubscribeButton } from '../../services/razorpayCheckout';
import { featureFlagApi } from '../../services/featureFlagApi';

type Frequency = 'full' | 'light';
type DurationKey = 'trial' | 'monthly' | 'quarterly' | 'half_yearly';

interface Tier {
  id: string;
  name: string;
  tagline: string;
  full: number;
  light: number;
  trial: { full: number; light: number };
  icon: React.ReactNode;
  popular?: boolean;
  gradient: string;
  border: string;
  iconBg: string;
  iconColor: string;
  buttonBg: string;
  ring: string;
}

// Mirrors backend TIER_PRICING / TRIAL_PRICING in app/api/routes/subscriptions.py.
const TIERS: Tier[] = [
  {
    id: 'basic',
    name: 'Basic',
    tagline: '30g protein per meal – perfect for getting started.',
    full: 15000,
    light: 8500,
    trial: { full: 1800, light: 900 },
    icon: <Utensils size={26} />,
    gradient: 'linear-gradient(to bottom, #FFFDF5, #FFF9E6)',
    border: 'var(--color-yellow)',
    iconBg: '#FFF3C4',
    iconColor: '#A67C00',
    buttonBg: 'var(--color-orange)',
    ring: 'rgba(255, 213, 79, 0.5)',
  },
  {
    id: 'premium',
    name: 'Elite',
    tagline: '40g protein per meal – comprehensive nutrition.',
    full: 17200,
    light: 9600,
    trial: { full: 2100, light: 1050 },
    icon: <Crown size={26} />,
    popular: true,
    gradient: 'linear-gradient(to bottom, #FCF1E9, #FFFBEB)',
    border: 'var(--color-orange)',
    iconBg: '#F8DCC8',
    iconColor: '#B8541E',
    buttonBg: 'var(--color-orange)',
    ring: 'rgba(210, 105, 30, 0.35)',
  },
  {
    id: 'elite',
    name: 'Premium',
    tagline: '50g protein per meal – the ultimate nutrition experience.',
    full: 19800,
    light: 10900,
    trial: { full: 2400, light: 1200 },
    icon: <Sparkles size={26} />,
    gradient: 'linear-gradient(to bottom, #FFFBEB, #FFF7ED)',
    border: '#FCD34D',
    iconBg: '#FEF3C7',
    iconColor: '#B45309',
    buttonBg: 'linear-gradient(to right, #D97706, #EA580C)',
    ring: 'rgba(217, 119, 6, 0.35)',
  },
];

const DURATIONS: { key: DurationKey; label: string; days: number; discount: number; badge: string }[] = [
  { key: 'trial', label: '3-Day Trial', days: 3, discount: 0, badge: 'Trial' },
  { key: 'monthly', label: '1 Month', days: 30, discount: 0, badge: 'Most Common' },
  { key: 'quarterly', label: '3 Months', days: 90, discount: 10, badge: 'Most Popular' },
  { key: 'half_yearly', label: '6 Months', days: 180, discount: 20, badge: 'Best Deal' },
];

const rupee = (n: number) => Math.round(n).toLocaleString('en-IN');

const whatsappLink = (text: string) =>
  `https://wa.me/919632815100?text=${encodeURIComponent(text)}`;

function quoteFor(tier: Tier, frequency: Frequency, durationKey: DurationKey) {
  const plan = DURATIONS.find((d) => d.key === durationKey)!;
  if (durationKey === 'trial') {
    const total = tier.trial[frequency];
    return { total, savings: 0, label: plan.label };
  }
  const monthlyPrice = tier[frequency];
  const baseTotal = monthlyPrice * (plan.days / 30);
  const total = baseTotal * (1 - plan.discount / 100);
  return { total, savings: baseTotal - total, label: plan.label };
}

export const MealPlansPage: React.FC = () => {
  useDocumentTitle(
    'Healthy Meal Plans | Weight Loss, Gym & Diabetes – The Good Food Way',
    'Explore healthy meal plans including weight loss, gym, diabetes, and intermittent fasting. Balanced meals with portion control by The Good Food Way.'
  );

  const [frequency, setFrequency] = useState<Frequency>('full');
  // Quarterly/half-yearly have no Razorpay link yet — default to monthly.
  const [durationKey, setDurationKey] = useState<DurationKey>('monthly');
  // Admin-controlled via the Feature Flags page (duration_quarterly /
  // duration_half_yearly) — defaults closed until the flags load, so we
  // never briefly show a duration that then disappears.
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  useEffect(() => {
    featureFlagApi.getFlags().then((res) => setFlags(res.data)).catch(() => {});
  }, []);
  const isDurationComingSoon = (key: DurationKey) =>
    key === 'quarterly' ? !flags.duration_quarterly
      : key === 'half_yearly' ? !flags.duration_half_yearly
      : key === 'trial' ? flags.duration_trial === false
      : key === 'monthly' ? flags.duration_monthly === false
      : false;
  // Tier flags default to visible until flags load, so cards never flash
  // then disappear once the real (likely-enabled) value arrives.
  const isTierHidden = (tierId: string) => flags[`tier_${tierId}`] === false;

  const toggleBtn = (active: boolean): React.CSSProperties => ({
    padding: '0.55rem 1.1rem',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: active ? '#fff' : 'transparent',
    color: active ? 'var(--color-orange)' : '#888',
    boxShadow: active ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
  });

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: topHtml }} />

      <section className="section-padding" id="plans" style={{ backgroundColor: 'var(--color-white)' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: '3rem', maxWidth: 800, marginLeft: 'auto', marginRight: 'auto' }}>
            <p style={{ fontWeight: 700, color: 'var(--color-orange)', fontSize: '0.8rem', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.2em', opacity: 0.9 }}>
              Calorie Counted &bull; Perfectly Balanced
            </p>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: 'var(--color-dark-green)', lineHeight: 1.2 }}>
              Choose a Plan That Works for You
            </h2>
            <p style={{ color: 'var(--color-grey)', fontSize: '1.05rem', lineHeight: 1.7, margin: '0 auto', maxWidth: 650 }}>
              Our meal plans are designed to remove complexity from healthy eating while keeping nutrition at the center.
              <br />
              <span style={{ fontWeight: 600, color: 'var(--color-dark-green)', fontSize: '0.95rem' }}>
                Delivered Monday &ndash; Saturday across Bangalore.
              </span>
            </p>
          </div>

          {/* Meal frequency toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-grey)', marginBottom: '0.6rem' }}>How many meals a day?</p>
            <div style={{ display: 'inline-flex', gap: '0.25rem', padding: '0.25rem', background: '#F3F3F0', borderRadius: '12px' }}>
              {(
                [
                  { key: 'full' as const, label: '2 Meals/Day', sub: '52 meals/month' },
                  { key: 'light' as const, label: '1 Meal/Day', sub: '26 meals/month' },
                ]
              ).map((f) => (
                <button key={f.key} type="button" style={toggleBtn(frequency === f.key)} onClick={() => setFrequency(f.key)}>
                  {f.label}
                  <span style={{ display: 'block', fontSize: '0.65rem', fontWeight: 400, opacity: 0.7 }}>{f.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Commitment duration toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '3rem' }}>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-grey)', marginBottom: '0.6rem' }}>Longer commitment, lower price</p>
            <div style={{ display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.25rem', padding: '0.25rem', background: '#F3F3F0', borderRadius: '12px' }}>
              {DURATIONS.map((d) => {
                // No Razorpay link exists yet for these two durations.
                const comingSoon = isDurationComingSoon(d.key);
                return (
                  <button
                    key={d.key}
                    type="button"
                    disabled={comingSoon}
                    title={comingSoon ? 'Coming soon' : undefined}
                    style={{
                      ...toggleBtn(durationKey === d.key),
                      ...(comingSoon ? { color: '#bbb', cursor: 'not-allowed' } : {}),
                    }}
                    onClick={() => !comingSoon && setDurationKey(d.key)}
                  >
                    {d.label}
                    {comingSoon ? (
                      <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', fontWeight: 700, color: '#999' }}>Soon</span>
                    ) : d.discount > 0 && (
                      <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', fontWeight: 700, color: '#2E7D32' }}>-{d.discount}%</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="plans-grid-mobile">
            {TIERS.filter((tier) => !isTierHidden(tier.id)).map((tier) => {
              const quote = quoteFor(tier, frequency, durationKey);
              const mealsLabel = frequency === 'light' ? '26 meals/month · Lunch OR Dinner' : '52 meals/month · Lunch + Dinner';
              const waText = `Hi! I'm interested in subscribing to The Good Food Way's ${tier.name} plan. Could you please share more details?`;
              return (
                <div
                  key={tier.id}
                  style={{
                    background: tier.gradient,
                    borderRadius: 20,
                    padding: '2rem',
                    boxShadow: tier.popular ? `0 4px 20px ${tier.ring}` : '0 4px 15px rgba(0,0,0,0.05)',
                    position: 'relative',
                    border: `2px solid ${tier.border}`,
                    outline: tier.popular ? `3px solid ${tier.ring}` : 'none',
                    outlineOffset: 2,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {tier.popular && (
                    <div className="special-offer-badge" style={{ transform: 'translate(-50%, -50%) scale(0.85)', top: 0, left: '50%', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <Star size={12} fill="currentColor" /> MOST POPULAR
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: tier.iconBg, color: tier.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {tier.icon}
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-dark-green)', margin: 0, letterSpacing: '-0.01em' }}>{tier.name}</h3>
                      <p style={{ fontSize: '0.8rem', color: 'var(--color-grey)', margin: 0 }}>{mealsLabel}</p>
                    </div>
                  </div>

                  <p style={{ fontSize: '0.95rem', color: 'var(--color-grey)', margin: '0 0 1.25rem' }}>{tier.tagline}</p>

                  <div style={{ marginBottom: '1.5rem', flexGrow: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem' }}>
                      <span style={{ fontSize: '0.9rem', color: '#666' }}>&#8377;</span>
                      <span style={{ fontSize: '2.3rem', fontWeight: 700, color: 'var(--color-dark-green)', lineHeight: 1 }}>{rupee(quote.total)}</span>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-grey)', margin: '0.25rem 0 0' }}>
                      for {quote.label.toLowerCase()}
                      {quote.savings > 0 && <span style={{ color: '#2E7D32', fontWeight: 600 }}> &middot; save &#8377;{rupee(quote.savings)}</span>}
                    </p>
                  </div>

                  {durationKey === 'trial' ? (
                    TRIAL_PAGE_URLS[frequency] ? (
                      <a
                        href={TRIAL_PAGE_URLS[frequency]}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'block', background: tier.buttonBg, color: 'white', textAlign: 'center',
                          padding: '0.8rem 1.5rem', borderRadius: 50, fontWeight: 700, fontSize: '1rem', textDecoration: 'none',
                        }}
                      >
                        Start Trial
                        <ArrowRight size={16} style={{ display: 'inline-block', marginLeft: '0.4rem', verticalAlign: 'middle' }} />
                      </a>
                    ) : (
                      <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#999' }}>Trial checkout coming soon</p>
                    )
                  ) : (
                    SUBSCRIPTION_BUTTON_IDS[tier.id]?.[frequency] ? (
                      <RazorpaySubscribeButton buttonId={SUBSCRIPTION_BUTTON_IDS[tier.id][frequency]!} />
                    ) : (
                      <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#999' }}>Subscription checkout coming soon</p>
                    )
                  )}
                  <a
                    href={whatsappLink(waText)}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      marginTop: '0.6rem',
                      fontSize: '0.8rem',
                      color: 'var(--color-grey)',
                      textDecoration: 'underline',
                    }}
                  >
                    Have questions? Chat on WhatsApp
                  </a>
                </div>
              );
            })}
          </div>

          <p style={{ textAlign: 'center', color: 'var(--color-grey)', fontSize: '0.85rem', marginTop: '2.5rem' }}>
            Need more protein? Add an extra 10g protein for just &#8377;50 per meal.
          </p>
        </div>
      </section>

      <div dangerouslySetInnerHTML={{ __html: bottomHtml }} />
    </>
  );
};

export default MealPlansPage;
